import React, { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, ImagePlus, Loader2, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/context/AdminAuthContext";
import { imgSrc } from "@/lib/img";
import { Card, PageTitle, Btn, Empty, Field, Input, Textarea, Toggle } from "@/admin/ui";

const EMPTY = { name: "", slug: "", description: "", image: null, hero_caption: "", hero_banner: null, feature_heading: "", feature_text: "", feature_image: null, why_choose_background: null, order: 99, visible: true, color_bg: "#DDE9C3", color_accent: "#5A7D1E" };

const ColorField = ({ label, value, onChange, testId }) => (
  <Field label={label}>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-picker`} className="w-11 h-10 rounded-lg border border-forest/15 bg-white cursor-pointer p-0.5" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="font-mono uppercase" />
    </div>
  </Field>
);

const CategoryForm = ({ item, onSaved, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY, ...item });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const fileRef = useRef(null);
  const bannerRef = useRef(null);
  const featureRef = useRef(null);
  const [uploadingFeature, setUploadingFeature] = useState(false);
  const whyRef = useRef(null);
  const [uploadingWhy, setUploadingWhy] = useState(false);
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image: data.url }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/upload", fd);
      setForm((f) => ({ ...f, hero_banner: data.url }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingBanner(false); }
  };

  const uploadFeature = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFeature(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/upload", fd);
      setForm((f) => ({ ...f, feature_image: data.url }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingFeature(false); }
  };

  const uploadWhy = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingWhy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/upload", fd);
      setForm((f) => ({ ...f, why_choose_background: data.url }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingWhy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      item?.id ? await adminApi.put(`/admin/categories/${item.id}`, payload) : await adminApi.post("/admin/categories", payload);
      toast.success("Category saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form data-testid="category-form" onSubmit={save} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name"><Input data-testid="category-name-input" value={form.name} onChange={on("name")} required placeholder="Moringa & Greens" /></Field>
        <Field label="Slug (URL)" hint="Leave blank to auto-generate from the name."><Input data-testid="category-slug-input" value={form.slug} onChange={on("slug")} placeholder="moringa-greens" /></Field>
      </div>
      <Field label="Short description"><Textarea data-testid="category-description-input" rows={2} value={form.description} onChange={on("description")} placeholder="Nature's powerhouse for everyday nutrition." /></Field>
      <div className="grid sm:grid-cols-[120px_1fr] gap-4 items-end">
        <Field label="Display order"><Input data-testid="category-order-input" type="number" value={form.order} onChange={on("order")} /></Field>
        <Toggle testId="category-visible-toggle" checked={form.visible} onChange={setVal("visible")} label={form.visible ? "Visible on hub page" : "Hidden"} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <ColorField label="Card background colour" value={form.color_bg} onChange={setVal("color_bg")} testId="category-color-bg-input" />
        <ColorField label="Number & title colour" value={form.color_accent} onChange={setVal("color_accent")} testId="category-color-accent-input" />
      </div>
      <div className="rounded-xl p-5 flex items-center justify-between" style={{ backgroundColor: form.color_bg }}>
        <div>
          <span className="font-serif text-3xl block" style={{ color: form.color_accent }}>01</span>
          <span className="font-serif text-lg" style={{ color: form.color_accent }}>{form.name || "Preview"}</span>
        </div>
        <span className="text-[11px] uppercase tracking-wide" style={{ color: form.color_accent }}>Live colour preview</span>
      </div>
      <Field label="Category image (optional)" hint="Shown in the corner of the card. Recommended ~848×1264 px (portrait, 2:3), PNG or JPG.">
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
        {form.image ? (
          <div className="relative inline-block">
            <img src={imgSrc(form.image)} alt="" className="w-24 h-24 rounded-xl object-cover border border-forest/15" />
            <button type="button" onClick={() => setForm((f) => ({ ...f, image: null }))} className="absolute -top-1 -right-1 bg-forest text-cream rounded-full p-1"><X size={11} /></button>
          </div>
        ) : (
          <button type="button" data-testid="category-image-button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-xs font-semibold text-forest border border-dashed border-forest/30 rounded-xl px-4 py-3 hover:border-gold hover:text-gold">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Upload image
          </button>
        )}
      </Field>

      <div className="border-t border-forest/10 pt-4 space-y-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70">Landing page hero <span className="font-normal normal-case tracking-normal text-mutedgreen">— edit the big banner at the top of this category's page</span></p>
        <Field label="Hero caption" hint="Short line shown under the category title on the landing page.">
          <Input data-testid="category-hero-caption-input" value={form.hero_caption} onChange={on("hero_caption")} placeholder="Balance. Focus. Inner strength." />
        </Field>
        <Field label="Hero banner image (optional)" hint="Wide background image for the landing page hero. Recommended ~1264×848 px (landscape, 3:2). Falls back to the default if empty.">
          <input ref={bannerRef} type="file" accept="image/*" onChange={uploadBanner} className="hidden" />
          {form.hero_banner ? (
            <div className="relative inline-block">
              <img src={imgSrc(form.hero_banner)} alt="" className="w-48 h-24 rounded-xl object-cover border border-forest/15" />
              <button type="button" data-testid="category-hero-banner-remove" onClick={() => setForm((f) => ({ ...f, hero_banner: null }))} className="absolute -top-1 -right-1 bg-forest text-cream rounded-full p-1"><X size={11} /></button>
            </div>
          ) : (
            <button type="button" data-testid="category-hero-banner-button" onClick={() => bannerRef.current?.click()} className="inline-flex items-center gap-2 text-xs font-semibold text-forest border border-dashed border-forest/30 rounded-xl px-4 py-3 hover:border-gold hover:text-gold">
              {uploadingBanner ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Upload banner
            </button>
          )}
        </Field>
      </div>

      <div className="border-t border-forest/10 pt-4 space-y-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70">Landing page feature panel <span className="font-normal normal-case tracking-normal text-mutedgreen">— the tall coloured panel on the left of this category's product grid</span></p>
        <Field label="Feature panel heading" hint="Big heading inside the panel. Falls back to the default if empty.">
          <Input data-testid="category-feature-heading-input" value={form.feature_heading} onChange={on("feature_heading")} placeholder="Nature's green energy in every scoop" />
        </Field>
        <Field label="Feature panel text" hint="Supporting lines under the heading. Falls back to the default if empty.">
          <Textarea data-testid="category-feature-text-input" rows={3} value={form.feature_text} onChange={on("feature_text")} placeholder={"Pure Plant Power.\nEveryday Greens."} />
        </Field>
        <Field label="Feature panel image (optional)" hint="Background image for the panel. Recommended ~848×1264 px (portrait, 2:3). Falls back to the default if empty.">
          <input ref={featureRef} type="file" accept="image/*" onChange={uploadFeature} className="hidden" />
          {form.feature_image ? (
            <div className="relative inline-block">
              <img src={imgSrc(form.feature_image)} alt="" className="w-24 h-32 rounded-xl object-cover border border-forest/15" />
              <button type="button" data-testid="category-feature-image-remove" onClick={() => setForm((f) => ({ ...f, feature_image: null }))} className="absolute -top-1 -right-1 bg-forest text-cream rounded-full p-1"><X size={11} /></button>
            </div>
          ) : (
            <button type="button" data-testid="category-feature-image-button" onClick={() => featureRef.current?.click()} className="inline-flex items-center gap-2 text-xs font-semibold text-forest border border-dashed border-forest/30 rounded-xl px-4 py-3 hover:border-gold hover:text-gold">
              {uploadingFeature ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Upload image
            </button>
          )}
        </Field>
      </div>

      <div className="border-t border-forest/10 pt-4 space-y-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70">"Why Choose Our…" background <span className="font-normal normal-case tracking-normal text-mutedgreen">— optional photo behind the "Why Choose Our…" benefits strip on this category's page</span></p>
        <Field label="Background image (optional)" hint="Shown behind the benefits, under a soft colour tint. Recommended ~1600×900 px (landscape, 16:9). Leave empty to keep the plain colour gradient.">
          <input ref={whyRef} type="file" accept="image/*" onChange={uploadWhy} className="hidden" />
          {form.why_choose_background ? (
            <div className="relative inline-block">
              <img src={imgSrc(form.why_choose_background)} alt="" className="w-48 h-24 rounded-xl object-cover border border-forest/15" />
              <button type="button" data-testid="category-why-bg-remove" onClick={() => setForm((f) => ({ ...f, why_choose_background: null }))} className="absolute -top-1 -right-1 bg-forest text-cream rounded-full p-1"><X size={11} /></button>
            </div>
          ) : (
            <button type="button" data-testid="category-why-bg-button" onClick={() => whyRef.current?.click()} className="inline-flex items-center gap-2 text-xs font-semibold text-forest border border-dashed border-forest/30 rounded-xl px-4 py-3 hover:border-gold hover:text-gold">
              {uploadingWhy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Upload background
            </button>
          )}
        </Field>
      </div>
      <div className="flex justify-end gap-2">
        <Btn type="button" variant="ghost" onClick={onCancel}>Cancel</Btn>
        <Btn type="submit" busy={busy} data-testid="category-form-save">Save</Btn>
      </div>
    </form>
  );
};

export default function Categories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => adminApi.get("/admin/categories").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await adminApi.delete(`/admin/categories/${c.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = async (c) => {
    try { await adminApi.put(`/admin/categories/${c.id}`, { ...c, visible: !c.visible }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (editing !== null) {
    return (
      <div>
        <PageTitle title={editing?.id ? "Edit category" : "New category"} />
        <Card className="p-6 sm:p-8"><CategoryForm item={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} /></Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-categories-page">
      <PageTitle title="Categories" sub="Manage the Shop by Category hub. Changes appear live on the site." action={<Btn data-testid="add-category-button" onClick={() => setEditing({})}><Plus size={14} /> Add category</Btn>} />
      <Card className="overflow-hidden">
        {items.length === 0 && <Empty text="No categories yet." />}
        <ul className="divide-y divide-forest/10">
          {items.map((c) => (
            <li key={c.id} data-testid={`admin-category-row-${c.id}`} className={`flex items-center gap-4 px-4 sm:px-6 py-4 ${c.visible === false ? "opacity-50" : ""}`}>
              <GripVertical size={16} className="text-forest/20 shrink-0" />
              <span className="w-10 h-10 rounded-lg shrink-0 flex items-center justify-center font-serif text-sm" style={{ backgroundColor: c.color_bg, color: c.color_accent }}>{c.order}</span>
              {c.image ? <img src={imgSrc(c.image)} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" /> : null}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-forest truncate">{c.name}</p>
                <p className="text-[11px] text-mutedgreen truncate">/{c.slug}{c.description ? ` · ${c.description}` : ""}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`toggle-category-${c.id}`} onClick={() => toggle(c)} className="hidden sm:block mr-2"><Toggle checked={c.visible !== false} onChange={() => toggle(c)} /></button>
                <button data-testid={`edit-category-${c.id}`} onClick={() => setEditing(c)} aria-label="Edit" className="p-2 rounded-full text-forest hover:bg-forest/5"><Pencil size={15} /></button>
                <button data-testid={`delete-category-${c.id}`} onClick={() => remove(c)} aria-label="Delete" className="p-2 rounded-full text-mutedgreen hover:text-red-700 hover:bg-red-50"><Trash2 size={15} /></button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
